/* eslint-disable */
/**
 * Dashboard Blog Admin Page — /dashboard/blog
 * Full CRUD: list, create, edit (modal), delete, preview link.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PenSquare, Plus, Trash2, Eye, Search, X, Save,
  BookOpen, Clock, Tag, AlertTriangle, CheckCircle,
} from 'lucide-react';
import { useBlog } from '../context/BlogContext';

const CATEGORIES = [
  'Hukum Keluarga',
  'Hukum Bisnis',
  'Hukum Ketenagakerjaan',
  'Hukum Perdata',
  'Hukum Pidana',
  'Lainnya',
];

const COVER_COLORS = [
  '#1a2d5a', '#0f3460', '#16213e', '#0a1628',
  '#1e3a6e', '#0d2137', '#1a1a2e', '#16213e',
];

const EMPTY_FORM = {
  title: '',
  category: CATEGORIES[0],
  excerpt: '',
  content: '',
  author: 'Tim Hukum Kantor Kami',
  status: 'published',
  coverColor: COVER_COLORS[0],
};

// Colour tokens (match dashboard)
const C = {
  bg:    '#0a1628',
  card:  '#0f1e38',
  border:'rgba(255,255,255,0.07)',
  gold:  '#f0c060',
  text:  '#e2e8f0',
  muted: 'rgba(255,255,255,0.45)',
  red:   '#f87171',
  green: '#34d399',
};

function s(extra = {}) {
  return {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    ...extra,
  };
}

function inputStyle(extra = {}) {
  return {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: '10px 14px',
    color: C.text,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    ...extra,
  };
}

/* ── Confirm Delete Dialog ── */
function ConfirmDialog({ post, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{ ...s(), padding: 28, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <AlertTriangle size={40} color={C.red} style={{ margin: '0 auto 16px' }} />
        <p style={{ color: C.text, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Hapus Artikel?</p>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>
          "<strong style={{ color: C.text }}>{post.title}</strong>" akan dihapus secara permanen.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={onCancel}
            style={{ padding: '10px 22px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 14 }}
          >
            Batal
          </button>
          <button
            onClick={onConfirm}
            style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: C.red, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
          >
            Hapus
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Post Form Modal ── */
function PostModal({ initialData, onSave, onClose }) {
  const [form, setForm] = useState(initialData || EMPTY_FORM);
  const [saved, setSaved] = useState(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;
    onSave(form);
    setSaved(true);
    setTimeout(onClose, 700);
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: 0.8 };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '24px 16px', overflowY: 'auto',
    }}>
      <div style={{ ...s(), width: '100%', maxWidth: 700, padding: 32, position: 'relative' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: 0 }}>
            {initialData ? 'Edit Artikel' : 'Tulis Artikel Baru'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Judul Artikel *</label>
            <input
              required
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Masukkan judul artikel..."
              style={inputStyle()}
            />
          </div>

          {/* Category + Status row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
            <div>
              <label style={labelStyle}>Kategori</label>
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                style={inputStyle()}
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                style={inputStyle()}
              >
                <option value="published">Terbit (Published)</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* Cover color picker */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Warna Cover</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {COVER_COLORS.map((col) => (
                <button
                  type="button"
                  key={col}
                  onClick={() => set('coverColor', col)}
                  style={{
                    width: 32, height: 32, borderRadius: 8, background: col, border: 'none', cursor: 'pointer',
                    outline: form.coverColor === col ? `3px solid ${C.gold}` : '3px solid transparent',
                    outlineOffset: 2, transition: 'outline 0.15s',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Author */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Penulis</label>
            <input
              value={form.author}
              onChange={(e) => set('author', e.target.value)}
              style={inputStyle()}
            />
          </div>

          {/* Excerpt */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Ringkasan (Excerpt) *</label>
            <textarea
              required
              rows={2}
              value={form.excerpt}
              onChange={(e) => set('excerpt', e.target.value)}
              placeholder="Ringkasan singkat artikel (ditampilkan di daftar blog)..."
              style={inputStyle({ resize: 'vertical', lineHeight: 1.5 })}
            />
          </div>

          {/* Content */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>
              Konten Artikel *{' '}
              <span style={{ fontSize: 10, color: C.muted, textTransform: 'none', letterSpacing: 0 }}>
                (gunakan ## untuk heading, **teks** untuk bold, - untuk bullet list)
              </span>
            </label>
            <textarea
              required
              rows={12}
              value={form.content}
              onChange={(e) => set('content', e.target.value)}
              placeholder="Tulis konten artikel di sini..."
              style={inputStyle({ resize: 'vertical', lineHeight: 1.7, fontFamily: 'monospace', fontSize: 13 })}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '10px 22px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 14 }}
            >
              Batal
            </button>
            <button
              type="submit"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 22px', borderRadius: 10, border: 'none',
                background: saved ? C.green : `linear-gradient(135deg, #c9930a, ${C.gold})`,
                color: '#0a1628', cursor: 'pointer', fontWeight: 700, fontSize: 14,
                transition: 'background 0.3s',
              }}
            >
              {saved ? <><CheckCircle size={15} /> Tersimpan</> : <><Save size={15} /> Simpan</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   MAIN: Blog Admin Page
══════════════════════════════════ */
export default function BlogAdminPage() {
  const navigate = useNavigate();
  const { posts, createPost, updatePost, deletePost } = useBlog();

  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);    // null | { mode: 'create' | 'edit', post?: {} }
  const [toDelete, setToDelete] = useState(null);

  const filtered = posts.filter((p) =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = (data) => {
    if (modal?.mode === 'edit' && modal.post) {
      updatePost(modal.post.id, data);
    } else {
      createPost(data);
    }
  };

  const handleDelete = () => {
    deletePost(toDelete.id);
    setToDelete(null);
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: C.bg, minHeight: '100%', padding: '4px 0 32px' }}>

      {/* Modals */}
      {modal && (
        <PostModal
          initialData={modal.post || null}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {toDelete && (
        <ConfirmDialog
          post={toDelete}
          onConfirm={handleDelete}
          onCancel={() => setToDelete(null)}
        />
      )}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: 0, textTransform: 'uppercase', letterSpacing: 2 }}>Blog &amp; Artikel</h1>
          <div style={{ width: 50, height: 2, background: C.gold, marginTop: 8, borderRadius: 2 }} />
          <p style={{ color: C.muted, fontSize: 13, marginTop: 8 }}>{posts.length} artikel total</p>
        </div>
        <button
          onClick={() => setModal({ mode: 'create' })}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: `linear-gradient(135deg, #c9930a, ${C.gold})`,
            color: '#0a1628', border: 'none', borderRadius: 10,
            padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            boxShadow: `0 4px 20px rgba(240,192,96,0.3)`,
          }}
        >
          <Plus size={16} /> Artikel Baru
        </button>
      </div>

      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`,
        borderRadius: 10, padding: '0 14px', height: 42, marginBottom: 20, maxWidth: 380,
      }}>
        <Search size={15} color={C.muted} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari artikel..."
          style={{ border: 'none', outline: 'none', background: 'transparent', color: C.text, fontSize: 14, width: '100%' }}
        />
      </div>

      {/* Table */}
      <div style={s({ overflow: 'hidden' })}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 90px 90px 110px',
          gap: 12,
          padding: '12px 20px',
          borderBottom: `1px solid ${C.border}`,
          fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase',
        }}>
          <span>Artikel</span>
          <span>Kategori</span>
          <span>Status</span>
          <span>Waktu Baca</span>
          <span style={{ textAlign: 'right' }}>Aksi</span>
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: C.muted }}>
            <BookOpen size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
            <p style={{ fontSize: 14 }}>Tidak ada artikel ditemukan.</p>
          </div>
        ) : (
          filtered.map((post, i) => (
            <div
              key={post.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 90px 90px 110px',
                gap: 12,
                padding: '14px 20px',
                borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : 'none',
                alignItems: 'center',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {/* Title + date */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div
                  style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: post.coverColor || '#1a2d5a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <BookOpen size={16} color="rgba(255,255,255,0.4)" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {post.title}
                  </p>
                  <p style={{ color: C.muted, fontSize: 11, margin: '2px 0 0' }}>
                    {new Date(post.publishedAt).toLocaleDateString('id-ID', { day:'numeric', month:'short', year:'numeric' })}
                  </p>
                </div>
              </div>

              {/* Category */}
              <span style={{
                fontSize: 11, fontWeight: 600, color: C.gold,
                background: 'rgba(240,192,96,0.1)', border: `1px solid rgba(240,192,96,0.2)`,
                borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                display: 'inline-block', maxWidth: '100%',
              }}>
                {post.category}
              </span>

              {/* Status */}
              <span style={{
                fontSize: 11, fontWeight: 600,
                color: post.status === 'published' ? C.green : C.muted,
                background: post.status === 'published' ? 'rgba(52,211,153,0.1)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${post.status === 'published' ? 'rgba(52,211,153,0.25)' : C.border}`,
                borderRadius: 20, padding: '3px 10px',
                display: 'inline-block',
              }}>
                {post.status === 'published' ? 'Terbit' : 'Draft'}
              </span>

              {/* Read time */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.muted, fontSize: 12 }}>
                <Clock size={11} /> {post.readTime}
              </span>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button
                  title="Lihat di halaman publik"
                  onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                  style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: C.muted, display: 'flex' }}
                >
                  <Eye size={14} />
                </button>
                <button
                  title="Edit artikel"
                  onClick={() => setModal({ mode: 'edit', post })}
                  style={{ background: 'rgba(240,192,96,0.1)', border: `1px solid rgba(240,192,96,0.2)`, borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: C.gold, display: 'flex' }}
                >
                  <PenSquare size={14} />
                </button>
                <button
                  title="Hapus artikel"
                  onClick={() => setToDelete(post)}
                  style={{ background: 'rgba(248,113,113,0.1)', border: `1px solid rgba(248,113,113,0.2)`, borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: C.red, display: 'flex' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
